import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Radio,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { ClosedMemberDuplicationCheckDTO } from '@/api/DTO/member-management/memberDuplication';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import {
	MatchedNameCell,
	MatchedText,
	ViewRecordCell,
} from './DuplicationCells';

export type ClosedMemberChoice =
	| { action: 'cancel' }
	| { action: 'skip' }
	| { action: 'link'; closedMember: ClosedMemberDuplicationCheckDTO };

interface Props {
	open: boolean;
	rows: ClosedMemberDuplicationCheckDTO[];
	onClose: (choice: ClosedMemberChoice) => void;
	/**
	 * Ask "Link to previous member record?" before closing. Off when the link
	 * is only sent later with another form (New register), which shows it.
	 */
	confirmLink?: boolean;
}

const formatDate = (value?: string | null) =>
	value ? dayjs(value).locale('en').format(DISPLAY_DATE_FORMAT) : '-';

/**
 * Step 2 of the member duplication check (DMS-REQ-WEB-CHA-002 §3.4):
 * matching service-closed members. Staff link one (`canLink` rows only),
 * continue without linking, or cancel.
 */
export default function ClosedMemberDuplicateDialog({
	open,
	rows,
	onClose,
	confirmLink = true,
}: Props) {
	const { t } = useTranslation();
	const [selected, setSelected] =
		useState<ClosedMemberDuplicationCheckDTO | null>(null);
	const [confirming, setConfirming] = useState(false);

	useEffect(() => {
		if (open) {
			setSelected(null);
			setConfirming(false);
		}
	}, [open]);

	const columns: GridColDef<ClosedMemberDuplicationCheckDTO>[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => {
				const radio = (
					<Radio
						checked={selected?.id === params.row.id}
						disabled={!params.row.canLink}
						onChange={() => setSelected(params.row)}
					/>
				);
				return params.row.canLink ? (
					radio
				) : (
					<Tooltip title={t('memberDuplication.cannotLinkTips')}>
						<span>{radio}</span>
					</Tooltip>
				);
			},
		},
		{ field: 'branchName', headerName: t('member.unit'), flex: 10 },
		{ field: 'fileNo', headerName: t('member.fileNo'), flex: 14 },
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 16,
			renderCell: (params) => (
				<MatchedNameCell
					nameEn={params.row.nameEn}
					nameZh={params.row.nameZh}
					matchedFields={params.row.matchedFields}
				/>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 11,
			renderCell: (params) => (
				<MatchedText
					value={params.row.phone}
					field='PHONE'
					matchedFields={params.row.matchedFields}
				/>
			),
		},
		{
			field: 'hkid',
			headerName: t('member.HKID'),
			flex: 11,
			renderCell: (params) => (
				<MatchedText
					value={params.row.hkid}
					field='HKID'
					matchedFields={params.row.matchedFields}
					hidden={!params.row.canSeen}
				/>
			),
		},
		{
			field: 'sexDesc',
			headerName: t('member.sex'),
			flex: 7,
			renderCell: (params) => params.row.sexDesc || '-',
		},
		{
			field: 'dateOfBirth',
			headerName: t('member.dateOfBirth'),
			flex: 11,
			renderCell: (params) =>
				params.row.canSeen
					? formatDate(params.row.dateOfBirth)
					: t('memberDuplication.hidden'),
		},
		{
			field: 'admissionDate',
			headerName: t('member.admissionDate'),
			flex: 11,
			renderCell: (params) => formatDate(params.row.admissionDate),
		},
		{
			field: 'dateOfExitService',
			headerName: t('member.dateExitService'),
			flex: 11,
			renderCell: (params) => formatDate(params.row.dateOfExitService),
		},
		{
			field: 'closedReasonDesc',
			headerName: t('member.closedReason'),
			flex: 14,
			renderCell: (params) =>
				[params.row.closedReasonDesc, params.row.closedReasonSpecify]
					.filter(Boolean)
					.join(' - ') || '-',
		},
		{
			field: 'view',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<ViewRecordCell
					id={params.row.id}
					status={params.row.status}
					canSeen={params.row.canSeen}
				/>
			),
		},
	];

	return (
		<Dialog
			open={open}
			onClose={() => onClose({ action: 'cancel' })}
			maxWidth={confirming ? 'sm' : 'xl'}
			fullWidth
		>
			<DialogTitle variant='h5'>
				{confirming
					? t('memberDuplication.linkConfirmTitle')
					: t('memberDuplication.closedTitle')}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={() => onClose({ action: 'cancel' })}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				{confirming ? (
					<Typography variant='body1'>
						{t('memberDuplication.linkConfirmText', {
							fileNo: selected?.fileNo,
						})}
					</Typography>
				) : (
					<>
						<Typography variant='body2' sx={{ marginBottom: '16px' }}>
							{t('memberDuplication.closedText')}
						</Typography>
						<LocalDataGrid
							rows={rows}
							columns={columns}
							getRowId={(row) => row.id}
							pagination={false}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions>
				{confirming ? (
					<>
						<Button
							sx={{ textTransform: 'none' }}
							onClick={() => setConfirming(false)}
						>
							{t('button.back')}
						</Button>
						<Button
							sx={{ textTransform: 'none' }}
							variant='contained'
							onClick={() =>
								selected && onClose({ action: 'link', closedMember: selected })
							}
						>
							{t('button.confirm')}
						</Button>
					</>
				) : (
					<>
						<Button
							sx={{ textTransform: 'none' }}
							onClick={() => onClose({ action: 'cancel' })}
						>
							{t('button.cancel')}
						</Button>
						<Button
							sx={{ textTransform: 'none' }}
							variant='outlined'
							onClick={() => onClose({ action: 'skip' })}
						>
							{t('memberDuplication.continueWithoutLinking')}
						</Button>
						<Button
							sx={{ textTransform: 'none' }}
							variant='contained'
							disabled={!selected}
							onClick={() => {
								if (!selected) return;
								if (confirmLink) setConfirming(true);
								else onClose({ action: 'link', closedMember: selected });
							}}
						>
							{t('memberDuplication.linkAndContinue')}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	);
}
